import React from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import { SEO } from "./SEO";
import { portfolioSeoByPath } from "../data/portfolioSeo";
import { getIndexableVideos, buildVideoObject } from "../data/portfolioVideos";
import { normalizeJsonLd } from "../utils/schema";
// import { buildCreativeWorkSchema } from "../utils/schema";

/**
 * Per-route SEO for portfolio pages, now including VideoObject JSON-LD.
 *
 * Before this change there was exactly ONE VideoObject on the entire site
 * (hardcoded in Home.jsx) and none on the ~100 pages that actually carry video.
 * Google cannot index a video it has no structured data for.
 *
 * Only videos flagged `mainContent: true` in src/data/portfolioVideos.js are
 * emitted. Decorative background loops are deliberately left out — claiming a
 * wallpaper loop as a VideoObject gets the markup ignored at best.
 *
 * NOTE: react-helmet only runs in the browser. Googlebot does render JS, but
 * relying on that for video is fragile, so scripts/prerender-all.mjs injects the
 * exact same JSON-LD into the static HTML at build time. Both read this same
 * registry, so they cannot disagree.
 */
/**
 * Portfolio routes whose page component renders its own <SEO>. The fallback
 * below must not fire for these or the page would emit two canonicals.
 *
 * Adding a /portfolio/* page that sets its own SEO without adding it here is a
 * silent bug — the page keeps working, it just quietly ships two canonical tags.
 * `node scripts/sync-sitemap-routes.mjs` checks this list against the router and
 * fails if the two drift, so keep that in CI rather than relying on memory.
 */
const SELF_MANAGED_PORTFOLIO_PATHS = new Set([
    "/portfolio/fragstalk",
    "/portfolio/anandhaas",
    "/portfolio/fruzo",
    "/portfolio/eustress",
    "/portfolio/acer-electric",
    "/portfolio/waylon-smith",
    "/portfolio/oval",
]);

/** "/portfolio/label/rare-suga" -> "Rare Suga" */
function brandFromPath(pathname) {
    const slug = pathname.split("/").filter(Boolean).pop() || "";
    return slug
        .split("-")
        .filter(Boolean)
        .map((w) => (/^[a-z]/.test(w) ? w[0].toUpperCase() + w.slice(1) : w))
        .join(" ");
}

/** The category segment, if the path has one: /portfolio/<category>/<slug> */
function categoryFromPath(pathname) {
    const parts = pathname.split("/").filter(Boolean); // ["portfolio", ...]
    return parts.length >= 3 ? parts[1] : null;
}

const CATEGORY_LABEL = {
    ui: "UI/UX Design",
    label: "Label & Packaging Design",
    branding: "Branding",
};

export default function PortfolioRouteSEO() {
    const location = useLocation();
    const pathname = location.pathname;
    const entry = portfolioSeoByPath[pathname];

    const pageUrl = entry?.url || `https://abox.agency${pathname}`;
    const videos = getIndexableVideos(pathname);
    const videoSchemas = videos.map((video) => buildVideoObject(video, pageUrl));

    // No portfolioSeo entry. Two cases.
    if (!entry) {
        const isPortfolioPath = pathname.startsWith("/portfolio/");
        const selfManaged = SELF_MANAGED_PORTFOLIO_PATHS.has(pathname);

        // 1. A portfolio case-study page with no entry of its own. ~38 of these
        //    render no <Helmet> at all, so without this they inherit index.html
        //    wholesale: no canonical, no unique title, and Google sees a set of
        //    identical untitled pages. A self-referencing canonical and a title
        //    derived from the slug is the floor. Anything richer belongs in
        //    portfolioSeo.js, which is also what puts the page in the sitemap.
        if (isPortfolioPath && !selfManaged) {
            const brand = brandFromPath(pathname);
            const category = categoryFromPath(pathname);
            const label = (category && CATEGORY_LABEL[category]) || "Case Study";
            const title = `${brand} ${label} | Abox Agency`;
            const description = `${brand} — ${label.toLowerCase()} work by Abox Agency, an ecommerce design and development studio.`;

            const fallbackSchema = [
                {
                    "@type": "CreativeWork",
                    name: `${brand} ${label}`,
                    url: pageUrl,
                    description,
                },
                ...videoSchemas,
            ];

            return (
                <SEO
                    title={title}
                    description={description}
                    url={pageUrl}
                    schema={fallbackSchema}
                />
            );
        }

        // 2. Not a portfolio page (or one that owns its SEO). Emit nothing but
        //    the VideoObject, if there is one — title and canonical belong to
        //    the page itself and must not be duplicated here.
        if (!videoSchemas.length) return null;

        const graph = normalizeJsonLd(videoSchemas);
        return (
            <Helmet>
                <script type="application/ld+json">{JSON.stringify(graph)}</script>
            </Helmet>
        );
    }

    // Merge the page schema with any VideoObjects. normalizeJsonLd() turns an
    // array into a single @graph, so this stays one valid JSON-LD block.
    const baseSchema = entry.schema
        ? [{ "@type": "CreativeWork", ...entry.schema }]
        : [];
    const combined = [...baseSchema, ...videoSchemas];

    // const schema = buildCreativeWorkSchema({
    //     name: `${entry.brand} Case Study`,
    //     url: entry.url,
    //     description: entry.description,
    //     image: entry.image,
    //     keywords: [entry.brand, entry.industry, ...(entry.services || [])].filter(Boolean),
    // });

    return (
        <SEO
            title={entry.title}
            description={entry.description}
            keywords={entry.keywords}
            url={entry.url}
            image={entry.image}
            services={entry.services}
            industry={entry.industry}
            // type="article"
            schema={combined.length ? combined : entry.schema}
        />
    );
}
